import { For, Show } from "solid-js";
import { cn } from "@mealplan/shared";

/** Altura/anchura mínima (en %) de una barra con valor > 0, para que sea visible. */
const MIN_VISIBLE_PCT = 6;
const MIN_VISIBLE_PCT_HORIZONTAL = 4;
const FULL_PCT = 100;

export interface BarDatum {
  /** Etiqueta visible bajo/junto a la barra. */
  label: string;
  /** Valor numérico (>= 0). */
  value: number;
  /** Texto accesible alternativo; por defecto `label: value`. */
  srLabel?: string;
}

interface BarChartProps {
  data: BarDatum[];
  /** Título accesible del gráfico (para screen readers). */
  caption: string;
  /** Sufijo de unidad para los valores mostrados (p. ej. "kcal"). */
  unitSuffix?: string;
  /** Orientación: barras verticales (columnas) u horizontales. */
  orientation?: "vertical" | "horizontal";
  class?: string;
}

/**
 * Gráfico de barras inline, theme-safe y accesible (Fase 5, eje 5).
 *
 * - Sin librería pesada: puro CSS/HTML sobre tokens del tema (`bg-accent`,
 *   `text-muted-foreground`, …), de modo que se adapta a cualquier tema.
 * - Accesible: se expone como `role="img"` con un `aria-label` que resume la
 *   serie, y además una tabla `sr-only` con los datos crudos para lectores de
 *   pantalla. Las barras visuales son `aria-hidden`.
 * - Respeta `prefers-reduced-motion` vía la clase `motion-safe:transition-*`.
 */
export function BarChart(props: BarChartProps) {
  const max = () => Math.max(1, ...props.data.map((d) => (Number.isFinite(d.value) ? d.value : 0)));
  const isHorizontal = () => props.orientation === "horizontal";

  const summary = () =>
    `${props.caption}. ${props.data
      .map(
        (d) =>
          `${d.srLabel ?? d.label}: ${d.value}${props.unitSuffix ? ` ${props.unitSuffix}` : ""}`,
      )
      .join("; ")}`;

  return (
    // role="img" + aria-label es la forma accesible canónica de exponer un
    // gráfico como una sola imagen con texto alternativo; no aplica <img>.
    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
    <figure class={cn("w-full", props.class)} role="img" aria-label={summary()}>
      {/* Tabla equivalente para lectores de pantalla. */}
      <table class="sr-only">
        <caption>{props.caption}</caption>
        <tbody>
          <For each={props.data}>
            {(d) => (
              <tr>
                <th scope="row">{d.srLabel ?? d.label}</th>
                <td>
                  {d.value}
                  {props.unitSuffix ? ` ${props.unitSuffix}` : ""}
                </td>
              </tr>
            )}
          </For>
        </tbody>
      </table>

      <Show
        when={isHorizontal()}
        fallback={
          <div aria-hidden="true" class="flex h-40 items-end justify-between gap-2">
            <For each={props.data}>
              {(d) => {
                const pct = () => Math.round((Math.max(0, d.value) / max()) * FULL_PCT);
                return (
                  <div class="flex min-w-0 flex-1 flex-col items-center gap-1">
                    <div class="flex h-32 w-full items-end justify-center">
                      <div
                        class="w-full max-w-9 rounded-t-md bg-accent/80 motion-safe:transition-[height] motion-safe:duration-500"
                        style={{ height: `${Math.max(d.value > 0 ? MIN_VISIBLE_PCT : 0, pct())}%` }}
                        title={`${d.label}: ${d.value}`}
                      />
                    </div>
                    <span class="w-full truncate text-center text-[10px] font-medium text-muted-foreground">
                      {d.label}
                    </span>
                    <span class="text-xs font-semibold tabular-nums">{d.value}</span>
                  </div>
                );
              }}
            </For>
          </div>
        }
      >
        <div aria-hidden="true" class="flex flex-col gap-2">
          <For each={props.data}>
            {(d) => {
              const pct = () => Math.round((Math.max(0, d.value) / max()) * FULL_PCT);
              return (
                <div class="flex items-center gap-3">
                  <span class="w-28 shrink-0 truncate text-xs font-medium text-muted-foreground">
                    {d.label}
                  </span>
                  <div class="h-5 flex-1 overflow-hidden rounded-md bg-accent/10">
                    <div
                      class="h-full rounded-md bg-accent/80 motion-safe:transition-[width] motion-safe:duration-500"
                      style={{
                        width: `${Math.max(d.value > 0 ? MIN_VISIBLE_PCT_HORIZONTAL : 0, pct())}%`,
                      }}
                    />
                  </div>
                  <span class="w-10 shrink-0 text-right text-xs font-semibold tabular-nums">
                    {d.value}
                  </span>
                </div>
              );
            }}
          </For>
        </div>
      </Show>
    </figure>
  );
}
